import {ActionIcon, Box, createStyles, getStylesRef, Group, Stack, Text, Tooltip} from '@mantine/core';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import useIsTruncated from 'ui-library/hooks/useIsTruncated';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import fuzzydate from 'util/fuzzydate';

import {InAppNotification, InAppNotificationStatus, PANEL_WIDTH} from './InAppNotificationConstants';
import NotificationBody from './notificationBody';

/**
 * Size of the unseen indicator dot in pixels. Intentionally sub-xs: a 4px dot reads as a speck and
 * an 8px dot crowds the title.
 */
const UNSEEN_DOT_SIZE_PX = 6;

const useStyles = createStyles((theme, {isUnread}: {isUnread: boolean}) => {
  const timestampRef = getStylesRef('timestamp');
  const dismissButtonRef = getStylesRef('dismissButton');
  return {
    row: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      borderBottom: `1px solid ${theme.colors.gray[2]}`,
      // Unread rows stay on a plain white background; read rows recede onto the lightest gray, one
      // step above the list's darker gray backdrop and end-of-list footer.
      backgroundColor: isUnread ? theme.white : theme.colors.gray[0],
      // The timestamp and dismiss button share the upper-right slot: the timestamp shows at rest and
      // the dismiss button takes its place when the row is hovered or holds keyboard focus.
      [`&:hover .${timestampRef}, &:focus-within .${timestampRef}`]: {
        visibility: 'hidden',
      },
      [`&:hover .${dismissButtonRef}, &:focus-within .${dismissButtonRef}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    title: {
      fontWeight: isUnread ? theme.other.fontWeights.bold : theme.other.fontWeights.medium,
      color: isUnread ? theme.colors.dark[9] : theme.colors.gray[7],
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    unseenDot: {
      flex: '0 0 auto',
      width: `${UNSEEN_DOT_SIZE_PX}px`,
      height: `${UNSEEN_DOT_SIZE_PX}px`,
      borderRadius: '50%',
      backgroundColor: theme.colors.blue[6],
    },
    body: {
      color: isUnread ? theme.colors.gray[7] : theme.colors.gray[6],
      fontSize: theme.fontSizes.sm,
      lineHeight: theme.lineHeight,
    },
    // Anchors the dismiss button on top of the timestamp so the two swap in place. The box sizes to
    // the timestamp (the absolutely-positioned button is out of flow)
    metaSlot: {
      position: 'relative',
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    timestamp: {
      ref: timestampRef,
      color: theme.colors.gray[5],
      fontSize: theme.fontSizes.xs,
      lineHeight: 1,
    },
    dismissButton: {
      ref: dismissButtonRef,
      position: 'absolute',
      top: '50%',
      // the visible X always sits at the far right of the timestamp regardless of its width
      right: '-10px',
      transform: 'translateY(-50%)',
      opacity: 0,
      pointerEvents: 'none',
      color: theme.colors.gray[5],
      '&& svg': {
        display: 'block',
        height: '12px',
        width: '12px',
      },
    },
    dismissIcon: {
      backgroundColor: 'transparent',
      color: theme.colors.gray[6],
      '&&:hover, &&:active, &&:focus': {
        backgroundColor: 'transparent',
        color: theme.colors.gray[8],
      },
    },
  };
});

interface NotificationRowProps {
  notification: InAppNotification;
  onLinkClick: (id: string, openedInPlace: boolean) => void;
  onDismiss: (id: string) => void;
}

/**
 * A single notification row: title with an unread dot (and a full-title tooltip when it truncates), a
 * body whose embedded link reports the click via `onLinkClick`, and an upper-right slot that shows a
 * relative timestamp at rest and swaps to a dismiss control on hover or keyboard focus.
 */
export default function NotificationRow({
  notification,
  onLinkClick,
  onDismiss,
}: NotificationRowProps): JSX.Element {
  const isUnread = notification.status === InAppNotificationStatus.UNREAD;
  const {classes} = useStyles({isUnread});
  // Only reveal the full-title tooltip when the title is actually clipped by the ellipsis.
  const {ref: titleRef, isTruncated: isTitleTruncated} = useIsTruncated<HTMLDivElement>([notification.title]);

  return (
    <Box
      className={classes.row}
      data-amp-safe={true}
      {...getTestProps({component: 'NotificationRow', element: 'root', key: notification.id})}
    >
      <Group position="apart" align="baseline" noWrap={true} spacing="md">
        <Group spacing="sm" align="center" noWrap={true} sx={{minWidth: 0}}>
          {isUnread && (
            <Box className={classes.unseenDot}>
              <span className="sr-only">Unseen</span>
            </Box>
          )}
          <Tooltip
            label={notification.title}
            openDelay={500}
            withinPortal={true}
            position="top-start"
            multiline={true}
            width={PANEL_WIDTH}
            disabled={!isTitleTruncated}
            events={{focus: true, hover: true, touch: false}}
          >
            <Text
              ref={titleRef}
              size="md"
              className={classes.title}
              // Only a truncated title needs to be reachable by keyboard, so its focus can reveal the tooltip.
              tabIndex={isTitleTruncated ? 0 : undefined}
            >
              {notification.title}
            </Text>
          </Tooltip>
        </Group>
        <Box className={classes.metaSlot}>
          <Text className={classes.timestamp}>
            {fuzzydate(notification.lastEventAt, {compact: true, showYear: false, showTime: false})}
          </Text>
          <Box className={classes.dismissButton}>
            <ActionIcon
              className={classes.dismissIcon}
              size={22}
              radius="xl"
              variant="subtle"
              color="gray"
              aria-label={`Dismiss "${notification.title}"`}
              onClick={() => {
                onDismiss(notification.id);
              }}
              data-amp-safe={true}
              {...getTestProps({component: 'NotificationRow', element: 'dismiss', key: notification.id})}
            >
              <BenchlingIcon icon="xmark" />
            </ActionIcon>
          </Box>
        </Box>
      </Group>
      {notification.body != null && (
        <Stack spacing={0} mt="xs">
          <Text className={classes.body}>
            <NotificationBody
              body={notification.body}
              onLinkClick={(openedInPlace) => {
                onLinkClick(notification.id, openedInPlace);
              }}
            />
          </Text>
        </Stack>
      )}
    </Box>
  );
}
